/**
 * Interstitial page shown right after the BMI question (HL-11982): the weight
 * the user could reach in 3 / 6 / 12 months, a projection chart that follows
 * the selected tab, and two health-benefit lines.
 *
 * Figma: desktop frame 7088:23730 (520 wide, content 488) and mobile frame
 * 7021:23355 (375 wide, content 343). The chart curve, dots, labels and axis
 * positions are taken verbatim from those frames (one geometry per shell);
 * the curve is drawn once and split into solid / dotted at the selected dot
 * with clip rectangles, so every tab state stays on the designed path.
 *
 * The page title is rendered by QuestionFrame via titleHtml; this component
 * starts at the target weight. The disclaimer under the CTA is exported
 * separately for the frame's footerNote slot. QuestionFrame mounts the page in
 * both shells, so every DOM id (gradient / clip ids included) is shell-scoped.
 */

import React, { useEffect, useRef, useState } from "react";
import * as translator from "../../../utils/translator";
import { useFunnel, useShell, useShellId, useShellIdFn } from "./shellContext";
import { PROJECTION_MONTHS, ProjectionMonths, projectedWeightAt } from "./constants";
import { formatWeight } from "./units";

const t = (language: string | undefined, key: string): string =>
  translator.translate(language, "default", "QuestionnairePage/" + key) || "";

const DEFAULT_MONTHS: ProjectionMonths = 6;

/** Two benefit rows: "{pct} {text} {highlight}" with the highlight in orange gradient. An empty pct
 *  (HL-12234 UK copy: one sentence with the number inside) drops the figure column. */
export const BENEFITS = [
  {
    pctKey: "weight_projection_benefit1_pct",
    textKey: "weight_projection_benefit1_text",
    highlightKey: "weight_projection_benefit1_highlight",
  },
  {
    pctKey: "weight_projection_benefit2_pct",
    textKey: "weight_projection_benefit2_text",
    highlightKey: "weight_projection_benefit2_highlight",
  },
] as const;

const HIGHLIGHT_GRADIENT = "linear-gradient(185.6deg, #ffb909 0.8%, #e17200 94.8%)";

// ---------- chart styling (Figma vectors) ----------
const CHART_FONT = "Roboto, Arial, sans-serif";
const LINE_COLOR = "#fdb324"; // Vector 22
const AREA_COLOR = "#f59504"; // Vector 21 gradient
const DOT_FILL = "#e88103"; // Ellipse 2557/2558: r4 + 1px white stroke
const LABEL_COLOR = "#f08e00";
const SELECTED_LABEL_COLOR = "#08080a";
const AXIS_COLOR = "#616975";
const GLOW_RINGS = [
  { r: 23.5, opacity: 0.1 }, // Ellipse 2561
  { r: 13.5, opacity: 0.2 }, // Ellipse 2559
];

// HL-12357 (WL3): the solid curve draws in on mount and slides to the selected dot on a tab change.
const CHART_ANIMATION_MS = 600;
const TAB_SLIDE_MS = 300;
/** Pointer travel (px) before a press on the tabs counts as a drag rather than a tap. */
const DRAG_THRESHOLD_PX = 6;

const prefersReducedMotion = (): boolean =>
  typeof window !== "undefined" && !!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

const easeOutCubic = (p: number): number => 1 - Math.pow(1 - p, 3);

/** Tweens towards `target` (from `from` on mount); jumps straight there when `animate` is off. */
function useTweenedNumber(target: number, from: number, animate: boolean): number {
  const [value, setValue] = useState(animate ? from : target);
  const valueRef = useRef(value);
  valueRef.current = value;

  useEffect(() => {
    if (!animate || prefersReducedMotion() || typeof requestAnimationFrame === "undefined") {
      setValue(target);
      return;
    }
    const start = valueRef.current;
    if (start === target) return;
    const startedAt = performance.now();
    let frame = requestAnimationFrame(function step(now) {
      const p = Math.min((now - startedAt) / CHART_ANIMATION_MS, 1);
      setValue(start + (target - start) * easeOutCubic(p));
      if (p < 1) frame = requestAnimationFrame(step);
    });
    return () => cancelAnimationFrame(frame);
  }, [target, animate]);

  return value;
}

type Point = { x: number; y: number };
type Anchor = "start" | "middle" | "end";
type Label = { x: number; y: number; anchor: Anchor };

interface ChartGeometry {
  width: number;
  height: number;
  /** Full Now -> 12 months curve (top edge of Vector 21), in frame coordinates. */
  curve: string;
  areaBaseY: number;
  /** x range of the curve; the area closes straight down at these edges. */
  curveX: [number, number];
  /** userSpaceOnUse gradient stops of the area fill. */
  gradientY: [number, number];
  dots: Point[];
  /** 10px value label per dot when it is not selected. */
  labels: Label[];
  /** 14px value label per dot when it is selected. */
  selectedLabels: Label[];
  axis: Label[];
}

const DESKTOP: ChartGeometry = {
  width: 488,
  height: 230,
  curve:
    "M -2 25.45 " +
    "C 34.971 47.764 83.673 77.648 142.708 90.201 " +
    "C 208.943 104.285 275.897 139.72 338.532 144.658 " +
    "C 398.199 149.362 451.563 166.525 489 166.525",
  areaBaseY: 224.45,
  curveX: [-2, 489],
  gradientY: [65.24, 202.81],
  dots: [
    { x: -0.5, y: 26.78 },
    { x: 131, y: 87.78 },
    { x: 314.5, y: 141.78 },
    { x: 467.5, y: 164.78 },
  ],
  labels: [
    { x: -4, y: 10.2, anchor: "start" },
    { x: 131, y: 70.2, anchor: "middle" },
    { x: 314.5, y: 124.2, anchor: "middle" },
    { x: 468, y: 143.2, anchor: "middle" },
  ],
  selectedLabels: [
    { x: 0, y: 5.2, anchor: "start" },
    { x: 132.5, y: 66.2, anchor: "middle" },
    { x: 316, y: 120.2, anchor: "middle" },
    { x: 469, y: 143.2, anchor: "middle" },
  ],
  axis: [
    { x: -2, y: 226.2, anchor: "start" },
    { x: 138, y: 226.2, anchor: "start" },
    { x: 291, y: 226.2, anchor: "start" },
    { x: 489, y: 226.2, anchor: "end" },
  ],
};

const MOBILE: ChartGeometry = {
  width: 342,
  height: 173,
  curve:
    "M 0 27 " +
    "C 25.677 42.817 59.5 64 100.5 72.898 " +
    "C 146.5 82.881 193 108 236.5 111.5 " +
    "C 277.938 114.834 315 127 341 127",
  areaBaseY: 168.06,
  curveX: [0, 341],
  gradientY: [55.21, 152.72],
  dots: [
    { x: 0.5, y: 26.5 },
    { x: 109.5, y: 74.5 },
    { x: 219.5, y: 110.5 },
    { x: 315.5, y: 124.5 },
  ],
  labels: [
    { x: 1, y: 9.9, anchor: "start" },
    { x: 113, y: 56.9, anchor: "middle" },
    { x: 219.5, y: 92.9, anchor: "middle" },
    { x: 316, y: 102.9, anchor: "middle" },
  ],
  selectedLabels: [
    { x: 1, y: 4.9, anchor: "start" },
    { x: 111, y: 52.9, anchor: "middle" },
    { x: 221, y: 88.9, anchor: "middle" },
    { x: 317, y: 102.9, anchor: "middle" },
  ],
  axis: [
    { x: 0, y: 169.9, anchor: "start" },
    { x: 108.5, y: 169.9, anchor: "middle" },
    { x: 217.5, y: 169.9, anchor: "middle" },
    { x: 342, y: 169.9, anchor: "end" },
  ],
};

interface WeightProjectionChartProps {
  language?: string;
  /** [now, 3 months, 6 months, 12 months] */
  weights: number[];
  /** Index into `weights` of the point the active tab highlights (1..3). */
  selectedIndex: number;
  animate?: boolean;
}

/** y on the dot polyline at `x`, so the glow travels along the curve while it animates. */
const dotLineY = (dots: Point[], x: number): number => {
  if (x <= dots[0].x) return dots[0].y;
  for (let i = 1; i < dots.length; i++) {
    const a = dots[i - 1];
    const b = dots[i];
    if (x <= b.x) return a.y + ((b.y - a.y) * (x - a.x)) / (b.x - a.x);
  }
  return dots[dots.length - 1].y;
};

const WeightProjectionChart: React.FC<WeightProjectionChartProps> = ({ language, weights, selectedIndex, animate = false }) => {
  const geo = useShell() === "mobile" ? MOBILE : DESKTOP;
  const fillId = useShellId("wl2_weight_projection_fill");
  const solidClipId = useShellId("wl2_weight_projection_clip_solid");
  const dottedClipId = useShellId("wl2_weight_projection_clip_dotted");
  const splitX = useTweenedNumber(geo.dots[selectedIndex].x, geo.dots[0].x, animate);
  const selected = { x: splitX, y: dotLineY(geo.dots, splitX) };
  // Generous bounds so round caps and the overflowing first / last dots survive the clip.
  const left = -20;
  const right = geo.width + 20;
  const areaPath = `${geo.curve} L ${geo.curveX[1]} ${geo.areaBaseY} L ${geo.curveX[0]} ${geo.areaBaseY} Z`;
  const axisLabels = [
    t(language, "weight_projection_axis_today"),
    ...PROJECTION_MONTHS.map((m) => t(language, `weight_projection_tab_${m}`)),
  ];

  return (
    <svg
      viewBox={`0 0 ${geo.width} ${geo.height}`}
      width={geo.width}
      height={geo.height}
      className="block w-full h-auto"
      overflow="visible"
      aria-hidden="true"
      focusable="false"
    >
      <defs>
        <linearGradient
          id={fillId}
          gradientUnits="userSpaceOnUse"
          x1="0"
          y1={geo.gradientY[0]}
          x2="0"
          y2={geo.gradientY[1]}
        >
          <stop offset="0" stopColor={AREA_COLOR} stopOpacity={0.4} />
          <stop offset="1" stopColor={AREA_COLOR} stopOpacity={0} />
        </linearGradient>
        <clipPath id={solidClipId}>
          <rect x={left} y={-30} width={selected.x - left} height={geo.height + 60} />
        </clipPath>
        <clipPath id={dottedClipId}>
          <rect x={selected.x} y={-30} width={right - selected.x} height={geo.height + 60} />
        </clipPath>
      </defs>

      <path d={areaPath} fill={`url(#${fillId})`} stroke="none" clipPath={`url(#${solidClipId})`} />
      <path
        d={geo.curve}
        fill="none"
        stroke={LINE_COLOR}
        strokeWidth={3.5}
        strokeLinecap="round"
        strokeDasharray="0.1 8.5"
        clipPath={`url(#${dottedClipId})`}
      />
      <path
        d={geo.curve}
        fill="none"
        stroke={LINE_COLOR}
        strokeWidth={3}
        strokeLinecap="round"
        strokeLinejoin="round"
        clipPath={`url(#${solidClipId})`}
      />

      {GLOW_RINGS.map((ring) => (
        <circle key={ring.r} cx={selected.x} cy={selected.y} r={ring.r} fill={LINE_COLOR} fillOpacity={ring.opacity} />
      ))}
      {geo.dots.map((p, i) => (
        <circle key={i} cx={p.x} cy={p.y} r={4} fill={DOT_FILL} stroke="#fff" strokeWidth={1} />
      ))}

      {geo.dots.map((_, i) => {
        const isSelected = i === selectedIndex;
        const label = isSelected ? geo.selectedLabels[i] : geo.labels[i];
        return (
          <text
            key={i}
            x={label.x}
            y={label.y}
            textAnchor={label.anchor}
            fontFamily={CHART_FONT}
            fontWeight={600}
            fontSize={isSelected ? 14 : 10}
            letterSpacing={isSelected ? -0.28 : -0.2}
            fill={isSelected ? SELECTED_LABEL_COLOR : LABEL_COLOR}
          >
            {formatWeight(weights[i], language)}
          </text>
        );
      })}
      {geo.axis.map((label, i) => (
        <text
          key={i}
          x={label.x}
          y={label.y}
          textAnchor={label.anchor}
          fontFamily={CHART_FONT}
          fontWeight={400}
          fontSize={10}
          letterSpacing={-0.2}
          fill={AXIS_COLOR}
        >
          {axisLabels[i]}
        </text>
      ))}
    </svg>
  );
};

// ---------- page ----------

interface WeightLossProjectionPageProps {
  language?: string;
  currentWeightKg: number;
}

const WeightLossProjectionPage: React.FC<WeightLossProjectionPageProps> = ({ language, currentWeightKg }) => {
  const [selectedMonths, setSelectedMonths] = useState<ProjectionMonths>(DEFAULT_MONTHS);
  const isWl3 = useFunnel() === "wl3";
  const scopeId = useShellIdFn();
  const weightId = useShellId("wl2_weight_projection_weight");
  const chartId = useShellId("wl2_weight_projection_chart");

  const weights = [currentWeightKg, ...PROJECTION_MONTHS.map((m) => projectedWeightAt(currentWeightKg, m))];
  const selectedIndex = PROJECTION_MONTHS.indexOf(selectedMonths) + 1;
  const tabId = (m: ProjectionMonths) => scopeId(`wl2_weight_projection_tab_${m}`);

  // HL-12357 (WL3): the highlight slides between tabs and can be dragged; release snaps to the nearest tab.
  const tablistRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{ pointerId: number; startX: number; segment: number; dragging: boolean } | null>(null);
  const suppressClickRef = useRef(false);
  const [dragOffsetPx, setDragOffsetPx] = useState<number | null>(null);
  const activeTabIndex = selectedIndex - 1;
  const lastTabIndex = PROJECTION_MONTHS.length - 1;

  const onTabsPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    const el = tablistRef.current;
    if (!el || (e.pointerType === "mouse" && e.button !== 0)) return;
    // 8px = the p-1 padding on both sides; the highlight moves in thirds of what is left.
    const segment = (el.clientWidth - 8) / PROJECTION_MONTHS.length;
    dragRef.current = { pointerId: e.pointerId, startX: e.clientX, segment, dragging: false };
  };

  const onTabsPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== e.pointerId) return;
    const dx = e.clientX - drag.startX;
    if (!drag.dragging) {
      if (Math.abs(dx) < DRAG_THRESHOLD_PX) return;
      drag.dragging = true;
      // Captured only once it is a drag, so a plain tap still clicks the tab under the finger.
      e.currentTarget.setPointerCapture?.(e.pointerId);
    }
    const min = -activeTabIndex * drag.segment;
    const max = (lastTabIndex - activeTabIndex) * drag.segment;
    setDragOffsetPx(Math.min(Math.max(dx, min), max));
  };

  const endTabsDrag = (e: React.PointerEvent<HTMLDivElement>, commit: boolean) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== e.pointerId) return;
    dragRef.current = null;
    if (!drag.dragging) return;
    // Swallow the click a drag can end with; cleared on the next tick in case none comes.
    suppressClickRef.current = true;
    setTimeout(() => {
      suppressClickRef.current = false;
    }, 0);
    if (commit) {
      const offset = Math.round((e.clientX - drag.startX) / drag.segment);
      const next = Math.min(Math.max(activeTabIndex + offset, 0), lastTabIndex);
      setSelectedMonths(PROJECTION_MONTHS[next]);
    }
    setDragOffsetPx(null);
  };

  const onTabClick = (m: ProjectionMonths) => {
    if (suppressClickRef.current) {
      suppressClickRef.current = false;
      return;
    }
    setSelectedMonths(m);
  };

  return (
    // Figma content is 343 wide on mobile (frame pads 20, design 16); desktop is 520 per HL-11982.
    <div className="flex flex-col items-center max-md:-mx-1 max-md:w-[calc(100%+8px)] md:w-[520px] md:mx-auto">
      <p
        id={weightId}
        className="w-full m-0 text-center font-['Poppins'] font-medium text-[35px] leading-[1.3] tracking-[-0.7px] text-[#08080a]"
      >
        {formatWeight(weights[selectedIndex], language)}
      </p>

      <div
        ref={isWl3 ? tablistRef : undefined}
        role="tablist"
        aria-label={t(language, "weight_projection_title")}
        className={`w-full h-[39px] mt-5 p-1 flex items-center border border-solid border-[#e4e0d9] rounded-[16px] ${
          isWl3 ? "relative select-none touch-pan-y" : ""
        }`}
        {...(isWl3
          ? {
              onPointerDown: onTabsPointerDown,
              onPointerMove: onTabsPointerMove,
              onPointerUp: (e: React.PointerEvent<HTMLDivElement>) => endTabsDrag(e, true),
              onPointerCancel: (e: React.PointerEvent<HTMLDivElement>) => endTabsDrag(e, false),
            }
          : {})}
      >
        {isWl3 && (
          <span
            aria-hidden="true"
            className="absolute left-1 top-1/2 h-[28px] w-[calc((100%-8px)/3)] rounded-[12px] bg-[#fbecd6] pointer-events-none"
            style={{
              transform: `translate(calc(${activeTabIndex * 100}% + ${dragOffsetPx ?? 0}px), -50%)`,
              transition:
                dragOffsetPx === null && !prefersReducedMotion()
                  ? `transform ${TAB_SLIDE_MS}ms cubic-bezier(0.22, 1, 0.36, 1)`
                  : "none",
            }}
          />
        )}
        {PROJECTION_MONTHS.map((m) => {
          const active = m === selectedMonths;
          return (
            <button
              key={m}
              type="button"
              role="tab"
              id={tabId(m)}
              aria-selected={active}
              aria-controls={chartId}
              onClick={() => (isWl3 ? onTabClick(m) : setSelectedMonths(m))}
              className={`flex-1 min-w-0 rounded-[12px] font-['Poppins'] text-[13px] leading-[21px] whitespace-nowrap cursor-pointer transition ${
                isWl3
                  ? `relative h-full bg-transparent ${active ? "text-[#e67d02] font-semibold" : "text-[#616975] font-medium hover:text-[#08080a]"}`
                  : active
                  ? "h-[28px] bg-[#fbecd6] text-[#e67d02] font-semibold"
                  : "h-full bg-transparent text-[#616975] font-medium hover:text-[#08080a]"
              }`}
              style={{ border: "none", padding: 0 }}
            >
              {t(language, `weight_projection_tab_${m}`)}
            </button>
          );
        })}
      </div>

      <div id={chartId} role="tabpanel" aria-labelledby={tabId(selectedMonths)} className="w-full mt-5 md:mt-8">
        <WeightProjectionChart language={language} weights={weights} selectedIndex={selectedIndex} animate={isWl3} />
      </div>

      {/* HL-12357: WL3 doubles the gap between the chart's month labels and the benefit rows. */}
      <div className={`flex flex-col max-md:items-center md:items-start gap-[9px] ${isWl3 ? "mt-12" : "mt-6"}`}>
        {BENEFITS.map((b) => {
          const pct = t(language, b.pctKey);
          return (
            <div key={b.pctKey} className={`flex items-center gap-[7px] ${pct ? "h-[23px]" : ""}`}>
              {pct && (
                <span className="md:w-[44px] shrink-0 font-['Poppins'] font-semibold text-[18px] leading-[1.25] text-[#f69605] whitespace-nowrap">
                  {pct}
                </span>
              )}
              <p className={`m-0 font-['Poppins'] text-[14px] leading-[1.25] text-black ${pct ? "whitespace-nowrap" : ""}`}>
                {`${t(language, b.textKey)} `}
                <span className="font-medium bg-clip-text text-transparent" style={{ backgroundImage: HIGHLIGHT_GRADIENT }}>
                  {t(language, b.highlightKey)}
                </span>
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
};

/** Small print under the Continue button; passed to QuestionFrame's footerNote. */
export const WeightLossProjectionDisclaimer: React.FC<{ language?: string }> = ({ language }) => (
  <p className="m-0 mt-6 mb-2 mx-auto max-w-[321px] text-center font-['Roboto'] text-[11px] leading-[1.3] tracking-[-0.22px] text-[#616975]">
    {t(language, "weight_projection_disclaimer")}
  </p>
);

export default WeightLossProjectionPage;
